import React from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { BellRing, Loader2, Minus, Moon, Plus, Timer } from 'lucide-react';

import PrivateLayout from '../layouts/PrivateLayout';
import { cn } from '../../lib/utils';
import { useSaveTeamSettings, useTeamSettings } from '../../services/onboardingBoard';

// Automations (Nic 2026-10-23: "keep it simple for Jorge"): what Sales and
// Onboarding do on their own, admins only (messages:manage). The automated
// texts' switches and Onboarding's auto stall days, moved out of the
// managers' Settings menus. Each text's words are its message (Edit words).
// Saves on every change.

const CARDS = [
  {
    key: 'nurture',
    icon: Moon,
    team: 'Sales',
    title: 'Auto nurture',
    detail: 'A Signing Up lead who misses a checkpoint moves to Nurture on its own.',
    textKey: 'potentialNurtureText',
    textLabel: 'Text them when they move to Nurture',
    message: 'auto_nurture',
  },
  {
    key: 'busy',
    icon: BellRing,
    team: 'Sales',
    title: 'Everyone busy',
    detail: 'A new lead who arrives while every rep is away (lunch, a meeting, a break) goes to whoever is back first, and their clock waits.',
    textKey: 'allBusyText',
    textLabel: 'Text them that someone will be in touch',
    message: 'auto_all_busy',
  },
  {
    key: 'stall',
    icon: Timer,
    team: 'Onboarding',
    title: 'Auto stall',
    detail: 'A client with no movement moves to Stalled after this many days.',
    dayKey: 'onboardingNurtureDay',
    min: 3,
    max: 60,
    textKey: 'onboardingNurtureText',
    textLabel: 'Text them when they move to Stalled',
    message: 'auto_stalled',
  },
];

const Switch = ({ on, disabled, onChange, label }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    aria-label={label}
    disabled={disabled}
    onClick={() => onChange(!on)}
    // Fixed track + explicit knob offsets: Bootstrap's button padding and
    // box model otherwise push the knob past the right edge.
    className="shrink-0 border-0 bg-transparent p-0 outline-none focus:outline-none focus-visible:ring-2 focus-visible:ring-sky-400/60 disabled:opacity-40"
  >
    <span className={cn('relative block rounded-full transition-colors', on ? 'bg-emerald-500' : 'bg-slate-300 dark:bg-white/15')} style={{ width: 36, height: 20 }}>
      <span className="absolute rounded-full bg-white shadow transition-[left] duration-150" style={{ width: 16, height: 16, top: 2, left: on ? 18 : 2 }} />
    </span>
  </button>
);

const Stepper = ({ value, min, max, disabled, onChange }) => (
  <div className="inline-flex shrink-0 items-center rounded-lg bg-white ring-1 ring-slate-200 dark:!bg-white/5 dark:ring-white/10">
    <button
      type="button"
      aria-label="Fewer days"
      disabled={disabled || value <= min}
      onClick={() => onChange(value - 1)}
      className="flex h-8 w-8 items-center justify-center rounded-l-lg text-slate-500 outline-none hover:bg-slate-100 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
    >
      <Minus size={13} />
    </button>
    <span className="w-9 text-center text-sm font-bold tabular-nums text-slate-800 dark:text-slate-100">{value}</span>
    <button
      type="button"
      aria-label="More days"
      disabled={disabled || value >= max}
      onClick={() => onChange(value + 1)}
      className="flex h-8 w-8 items-center justify-center rounded-r-lg text-slate-500 outline-none hover:bg-slate-100 focus:outline-none disabled:opacity-30 dark:text-slate-400 dark:hover:!bg-white/10"
    >
      <Plus size={13} />
    </button>
  </div>
);

const AutomationsPage = () => {
  const q = useTeamSettings();
  const save = useSaveTeamSettings();
  const settings = q.data?.settings || {};
  const canEdit = q.data?.canEdit || {};

  const put = async (patch, done) => {
    try {
      await save.mutateAsync(patch);
      toast.success(done);
    } catch (e) {
      toast.error(e?.response?.data?.msg || "That didn't save. Please try again.");
    }
  };

  return (
    <PrivateLayout>
      <div className="mx-auto max-w-2xl py-4">
        <h2 className="mb-0 text-[17px] font-bold text-slate-800 dark:text-slate-100">Automations</h2>
        <p className="mb-4 text-xs text-slate-400">
          What Sales and Onboarding do on their own. The words of each text are on the{' '}
          <Link to="/operations/messages" className="font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-300">
            Messages
          </Link>{' '}
          page. Changes save right away.
        </p>

        {q.isLoading ? (
          <div className="flex h-32 items-center justify-center">
            <Loader2 size={18} className="animate-spin text-slate-400" />
          </div>
        ) : q.isError ? (
          <div className="rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:!bg-rose-500/10 dark:text-rose-300">
            Automations couldn&apos;t be loaded.
          </div>
        ) : (
          CARDS.map((c) => {
            const Icon = c.icon;
            const text = settings[c.textKey] || { enabled: false };
            const canText = !!canEdit[c.textKey];
            const canDay = !!c.dayKey && !!canEdit[c.dayKey];
            return (
              <section
                key={c.key}
                aria-label={c.title}
                className="mb-3 rounded-xl bg-white p-4 shadow-sm ring-1 ring-slate-900/5 dark:!bg-surface-card dark:ring-white/10"
              >
                <div className="flex items-start gap-3">
                  <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-indigo-50 text-indigo-600 dark:!bg-indigo-500/15 dark:text-indigo-300">
                    <Icon size={15} />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-bold text-slate-800 dark:text-slate-100">{c.title}</span>
                      <span className="rounded-full bg-slate-100 px-2 py-0.5 text-[10.5px] font-semibold text-slate-500 dark:!bg-white/10 dark:text-slate-400">
                        {c.team}
                      </span>
                    </div>
                    <p className="mb-0 mt-0.5 text-xs text-slate-500 dark:text-slate-400">{c.detail}</p>
                  </div>
                  {c.dayKey ? (
                    <div className="flex shrink-0 items-center gap-2">
                      <Stepper
                        value={Number(settings[c.dayKey]) || 14}
                        min={c.min}
                        max={c.max}
                        disabled={!canDay || save.isLoading}
                        onChange={(v) => put({ [c.dayKey]: v }, `Auto stall: ${v} days.`)}
                      />
                      <span className="text-xs text-slate-500 dark:text-slate-400">days</span>
                    </div>
                  ) : null}
                </div>
                <div className="mt-3 flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 dark:!bg-white/[0.04]">
                  <span className="flex-1 text-[13px] font-medium text-slate-600 dark:text-slate-300">{c.textLabel}</span>
                  <Link
                    to={`/operations/messages?key=${c.message}`}
                    className="whitespace-nowrap text-[12px] font-semibold text-indigo-600 hover:text-indigo-500 dark:text-indigo-300"
                  >
                    Edit words
                  </Link>
                  <Switch
                    on={!!text.enabled}
                    disabled={!canText || save.isLoading}
                    label={c.textLabel}
                    onChange={(v) => put({ [c.textKey]: { enabled: v } }, `${c.title} text ${v ? 'on' : 'off'}.`)}
                  />
                </div>
              </section>
            );
          })
        )}
      </div>
    </PrivateLayout>
  );
};

export default AutomationsPage;
